import React, { useState } from 'react';
import { Home, TrendingUp, Clock, BarChart3, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import AiDisclaimer from './AiDisclaimer';

// Design shows these 5 AI Insight sections in a left icon rail (desktop) /
// bottom icon nav (mobile), in this order. The underlying tab index (used
// by AiIntegration's buildAiContent() switch) is a different, pre-existing
// order, so this array just maps design position -> that existing index -
// no tab-switching logic needed to change.
const SECTIONS = [
  { tabIndex: 1, label: "Firms & Estimate", icon: Home },
  { tabIndex: 0, label: "Case Outlook", icon: TrendingUp },
  { tabIndex: 2, label: "Timeline & Evidence", icon: Clock },
  { tabIndex: 4, label: "Case Analysis", icon: BarChart3 },
  { tabIndex: 3, label: "Risks & Defenses", icon: ShieldCheck },
];

function AiSectionRail({ currentAiTab, setCurrentAiTab, content }) {
  const [collapsed, setCollapsed] = useState(false);
  const activePos = SECTIONS.findIndex((s) => s.tabIndex === currentAiTab);
  const nextSection = SECTIONS[activePos + 1];

  const goTo = (tabIndex) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentAiTab(tabIndex);
  };

  return (
    <div className="w-full font-plexsans">
      <div className="w-full rounded-2xl border border-[rgba(20,33,61,.08)] bg-ivory shadow-[0_1px_3px_rgba(20,33,61,.06)] p-5 lg:p-7">
        <div className="flex flex-col lg:flex-row w-full items-start gap-5 lg:gap-8">
          {/* Desktop left rail */}
          <div
            className={
              "hidden lg:flex flex-col shrink-0 transition-[width] duration-200 " +
              (collapsed ? "w-[52px]" : "w-[220px]")
            }
          >
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="flex items-center gap-1 text-[13px] text-[rgba(20,33,61,.5)] hover:text-navy mb-3 px-1 w-max"
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              {!collapsed && <span>Collapse</span>}
            </button>
            <div className="flex flex-col gap-1.5">
              {SECTIONS.map((s) => {
                const active = s.tabIndex === currentAiTab;
                const Icon = s.icon;
                return (
                  <button
                    type="button"
                    key={s.tabIndex}
                    onClick={() => goTo(s.tabIndex)}
                    title={collapsed ? s.label : undefined}
                    className={
                      "flex items-center gap-2.5 rounded-lg px-3.5 py-3 text-left text-[14px] font-medium transition-colors " +
                      (collapsed ? "justify-center px-0" : "") + " " +
                      (active
                        ? "bg-tan text-white"
                        : "text-[rgba(20,33,61,.6)] hover:bg-[rgba(20,33,61,.05)]")
                    }
                  >
                    <Icon size={17} className="flex-none" />
                    {!collapsed && <span className="truncate">{s.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 w-full min-w-0 pb-20 lg:pb-0">
            {content}
            {nextSection && (
              <div className="flex justify-start mt-3">
                <button
                  type="button"
                  onClick={() => goTo(nextSection.tabIndex)}
                  className="inline-flex items-center gap-2 rounded-lg bg-tan px-5 py-3 text-[14px] font-semibold text-white hover:bg-tanDark transition-colors"
                >
                  See {nextSection.label}
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            )}
            {/* Lives inside this card (not as a separate element below it)
                so it reads as part of the AI insights, not as one more line
                in the site footer directly beneath it. Shown on every
                section/tab, same as before. */}
            <AiDisclaimer />
          </div>
        </div>
      </div>

      {/* Mobile bottom icon nav */}
      <div className="flex lg:hidden fixed bottom-0 left-0 right-0 z-30 items-center justify-around border-t border-[rgba(20,33,61,.1)] bg-ivory py-2 shadow-[0_-2px_8px_rgba(20,33,61,.06)]">
        {SECTIONS.map((s) => {
          const active = s.tabIndex === currentAiTab;
          const Icon = s.icon;
          return (
            <button
              type="button"
              key={s.tabIndex}
              onClick={() => goTo(s.tabIndex)}
              aria-label={s.label}
              className={
                "flex items-center justify-center w-11 h-11 rounded-lg transition-colors " +
                (active ? "bg-tan text-white" : "text-[rgba(20,33,61,.45)]")
              }
            >
              <Icon size={19} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default AiSectionRail;
